<template>
  <PageContentLayoutOnly headerColor="#fafafa">
    <div workorderdetails class="notification headerColor" v-if="formData">
      <div class="box is-12">
        <ValidationObserver ref="form" v-slot="{ handleSubmit }">
          <transition name="fade" mode="out-in" appear>
            <form @submit.prevent="handleSubmit(onFormSubmit)" class="form">
              <fieldset>
                <div class="pb-5 font-bold">
                  {{ `Work Order ID - ${workOrderID}` }}
                </div>
                <div class="columns">
                  <div class="column is-6">
                    <b-field :label="locationTypeText">
                      <b-select
                        @blur="locationTypeChanged()"
                        v-model="formData.locationType"
                        required="true"
                        placeholder="Select a Location Type"
                      >
                        <option
                          v-for="option in locationTypeList"
                          :value="option.value"
                          :key="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </b-select>
                    </b-field>
                  </div>
                </div>
                <div class="columns">
                  <div
                    class="column is-6"
                    v-if="formData && formData.locationType === 'My Property'"
                  >
                    <label class="label">Select Location / Unit</label>
                    <b-autocomplete
                      size="is-small"
                      field="label"
                      icon="magnify"
                      :loading="isFetching"
                      placeholder="e.g. Location / Unit Search"
                      :data="addressData"
                      :open-on-focus="openOnFocus"
                      v-model="addressFilterQuery"
                      required="true"
                      @typing="getAsyncData"
                      @select="option => (selected = option)"
                    >
                    </b-autocomplete>
                  </div>
                </div>
                <div class="columns">
                  <div class="column is-6">
                    <label class="label">Select Vendor</label>
                    <b-autocomplete
                      size="is-small"
                      field="label"
                      icon="magnify"
                      :loading="isFetching"
                      placeholder="e.g. Vendor"
                      :data="vendorData"
                      :open-on-focus="openOnFocus"
                      v-model="vendorFilterQuery"
                      required="true"
                      @typing="getVendorAsyncData"
                      @select="option => (selected = option)"
                    >
                    </b-autocomplete>
                  </div>
                  <div class="column is-6">
                    <b-field label="Completed Date">
                      <b-datepicker
                        v-model="formData.completedDate"
                        label="Complete By Date"
                        :show-week-number="showWeekNumber"
                        placeholder="Click to select Completed Date..."
                        icon="calendar-today"
                        trap-focus
                        editable
                        :disabled="true"
                      ></b-datepicker>
                    </b-field>
                  </div>
                </div>
                <div class="columns">
                  <div class="column is-6" v-if="workOrderTypeList">
                    <valid-select
                      label="Work Order Type"
                      placeholder="Work Order Types"
                      vid="workOrderTypeID"
                      required="true"
                      rules="required"
                      v-model="formData.workOrderTypeID"
                    >
                      <option
                        v-for="option in workOrderTypeList"
                        :value="option.value"
                        :key="option.value"
                        >{{ option.label }}</option
                      >
                    </valid-select>
                  </div>
                  <div class="column is-12">
                    <b-field label="Sent to Vendor Date">
                      <b-datepicker
                        v-model="formData.sentToVendorDate"
                        label="Sent To Vendor Date"
                        :show-week-number="showWeekNumber"
                        placeholder="Click to select Sent to Vendor Date..."
                        icon="calendar-today"
                        trap-focus
                        editable
                      ></b-datepicker>
                      <input
                        type="hidden"
                        id="TestCompletedDate"
                        v-model="formData.completedDate"
                      />
                    </b-field>
                  </div>
                </div>
                <div class="columns is-12">
                  <div class="column is-6">
                    <b-field label="Complete By Date">
                      <b-datepicker
                        v-model="formData.completeByDate"
                        label="Complete By Date"
                        :show-week-number="showWeekNumber"
                        placeholder="Click to select Complete By Date..."
                        icon="calendar-today"
                        trap-focus
                        editable
                      ></b-datepicker>
                    </b-field>
                  </div>
                  <div
                    class="column is-6"
                    :style="
                      formData.cancelledDate !== undefined && formData.cancelledDate
                        ? 'display: block;'
                        : 'display: none;'
                    "
                  >
                    <b-field label="Cancellation Date">
                      <b-datepicker
                        v-model="formData.cancelledDate"
                        label="Cancellation Date"
                        :show-week-number="showWeekNumber"
                        placeholder="Click to select Cancellation Date..."
                        icon="calendar-today"
                        trap-focus
                        aria-readonly="true"
                        readonly
                        :disabled="true"
                      ></b-datepicker>
                    </b-field>
                  </div>
                </div>
                <div class="is-12 pt-5" v-show="false">
                  <valid-input
                    style="width:50px !important;"
                    name="CompletedNote"
                    label="Completed Notes"
                    type="textarea"
                    vid="CompletedNote"
                    placeholder="Completed Notes"
                    spellcheck="true"
                    aria-label="Completed Notes"
                    rules="max:8000"
                    class="is-small"
                    v-model="formData.completedNote"
                    :disabled="true"
                  />
                  <input type="hidden" id="TestCompletedNote" v-model="formData.completedNote" />
                </div>

                <div class="is-12 pt-5">
                  <valid-input
                    style="width:50px !important;"
                    name="Summary"
                    label="Title"
                    type="text"
                    vid="Summary"
                    placeholder="Title"
                    spellcheck="true"
                    aria-label="Title"
                    rules="max:100|required"
                    class="is-small"
                    :disabled="
                      formData.status &&
                      (formData.status === 'cancelled' || formData.status === 'promoted')
                        ? true
                        : false
                    "
                    v-model="formData.description"
                  />
                </div>
                <div class="is-12 pt-5">
                  <valid-input
                    style="width:50px !important;"
                    name="Description"
                    label="Description"
                    type="textarea"
                    vid="Description"
                    placeholder="Description"
                    spellcheck="true"
                    aria-label="Description"
                    rules="required|max:8000"
                    class="is-small"
                    v-model="formData.detailedDescription"
                  />
                </div>

                <div class="is-12 pt-5">
                  <valid-input
                    style="width:50px !important;"
                    name="LocationDescription"
                    label="Location Description"
                    type="textarea"
                    vid="LocationDescription"
                    placeholder="Location Description"
                    spellcheck="true"
                    aria-label="Location Description"
                    rules="max:200"
                    class="is-small"
                    v-model="formData.locationDescription"
                  />
                </div>
                <div class="is-12 pt-5 pb-2">
                  <valid-input
                    tests-id="special-instructions-input"
                    style="width:50px !important;"
                    name="SpecialInstructions"
                    label="Special Instructions"
                    type="textarea"
                    vid="SpecialInstructions"
                    id="SpecialInstructions"
                    placeholder="Special Instructions"
                    spellcheck="true"
                    aria-label="Special Instructions"
                    rules="max:8000"
                    class="is-small"
                    v-model="formData.specialInstructions"
                  />
                  <input
                    id="TestSpecialInstructions"
                    type="hidden"
                    v-model="formData.specialInstructions"
                  />
                </div>
                <div
                  class="is-12 pt-5 pb-2"
                  :style="
                    formData.cancelledDate !== undefined && formData.cancelledDate
                      ? 'display: block;'
                      : 'display: none;'
                  "
                >
                  <valid-input
                    style="width:50px !important;"
                    name="CancellationReason"
                    label="Cancellation Reason"
                    type="textarea"
                    vid="CancellationReason"
                    id="CancellationReason"
                    placeholder="Cancellation Reason"
                    spellcheck="true"
                    aria-label="Cancellation Reason"
                    rules="max:8000"
                    class="is-small"
                    v-model="formData.cancelledReason"
                    aria-readonly="true"
                    :disabled="true"
                  />
                </div>
                <div @click="hideShowMap" style="cursor: pointer" class="href-overflow-wrap pt-5">
                  {{ hideShowMapText }}
                  <i
                    style="font-size: 8pt; vertical-align: baseline !important;"
                    class="icon-map"
                  />
                </div>
                <div class="font-bold">
                  Please drag and drop the pin to represent the location of the work order request.
                </div>
                <div
                  class="pt-6 pb-6"
                  v-if="hideShowMapFlag && (hideShowMapFlag === true || hideShowMapFlag === 'true')"
                >
                  <GoogleMap
                    mapStyle="satellite"
                    :accessToken="accessToken"
                    :longitude="
                      Number.parseFloat(
                        formData.longitude && formData.longitude !== 0 ? formData.longitude : 0
                      )
                    "
                    :latitude="
                      Number.parseFloat(
                        formData.latitude && formData.latitude !== 0 ? formData.latitude : 0
                      )
                    "
                    :zoomLevel="parseInt(19)"
                    :workOrderId="parseInt(workOrderID)"
                    :reloadParent="reloadParentImages"
                    :showUpload="false"
                  />
                </div>
                <div class="columns pt-5">
                  <div class="column is-1 pr-2">
                    <button
                      type="submit"
                      :disabled="loading"
                      class="button is-primary is-rounded"
                      tests-id="update-work-order-button"
                    >
                      Update
                    </button>
                  </div>
                  <div v-if="!formData.completedDate" class="column is-2 pl-6">
                    <button
                      @click.prevent="completeWorkOrder()"
                      type="button"
                      :disabled="loading"
                      class="button is-primary is-rounded"
                    >
                      Complete Work Order
                    </button>
                  </div>
                  <div v-if="formData.completedDate" class="column is-2 pl-6">
                    <button
                      @click.prevent="confirmResetCompletedToIncomplete()"
                      type="button"
                      :disabled="loading"
                      class="button is-primary is-rounded"
                    >
                      Reset to Incomplete
                    </button>
                  </div>
                </div>
              </fieldset>
              <fieldset>
                <ValidationProvider vid="providerErrors" v-slot="{ errors }">
                  <div class="field">
                    <span>{{ errors[0] }}</span>
                  </div>
                </ValidationProvider>
              </fieldset>
            </form>
          </transition>
        </ValidationObserver>
      </div>
    </div>
    <Modal :toggle.sync="completeToggle">
      <CompleteModal :workOrderID="parseInt(workOrderID)" :workOrder="currentWorkOrder" />
    </Modal>
  </PageContentLayoutOnly>
</template>

<script>
import PageContentLayoutOnly from '@/containers/PageContentLayoutOnly'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { mapState, mapGetters } from 'vuex'
import CompleteModal from '@/components/WorkOrderCompleteModal'
import Modal from '@/components/Modal'
import GoogleMap from '@/components/googlemaps'
import { data } from './keys/data'
import { methods } from './keys/methods'

// components
import ValidInput from '@/components/inputs/ValidInput'
import ValidSelect from '@/components/inputs/ValidSelect'

export default {
  name: 'UpdateWorkOrder',
  components: {
    ValidInput,
    ValidSelect,
    ValidationObserver,
    ValidationProvider,
    PageContentLayoutOnly,
    CompleteModal,
    Modal,
    GoogleMap
  },

  data,

  computed: {
    ...mapState({
      hoaId: state => state.user.selectedHoaId,
      ...mapGetters('user', ['authUser'])
    }),
    locationFilterSuggestions() {
      if (
        this.addressFilterQuery &&
        this.addressDropDownList &&
        this.addressDropDownList != undefined
      ) {
        return this.addressDropDownList.filter(
          a => a.label.indexOf(this.addressDropDownList.toLowerCase()) !== -1
        )
      } else {
        return this.addressDropDownList
      }
    },
    vendorFilterSuggestions() {
      if (
        this.vendorFilterQuery &&
        this.vendorDropDownList &&
        this.vendorDropDownList != undefined
      ) {
        return this.vendorDropDownList.filter(
          a => a.label.indexOf(this.vendorDropDownList.toLowerCase()) !== -1
        )
      } else {
        return this.vendorDropDownList
      }
    }
  },

  mounted() {
    this.reload()
  },

  watch: {
    hoaId() {
      this.reload()
    },
    completeToggle() {
      if (this.completeToggle == false) {
        if (this.isDebug == true) console.debug('...in completeToggle()')
        this.completeToggle = false
        if (this.Modal && this.Modal != undefined) {
          this.Modal.$emit('update:toggle', (this.toggle = false))
        }
        this.reload()
      }
    }
  },

  methods,

  i18n: {
    messages: {
      en: {
        workOrderUpdate: { title: 'Update Work Order' },
        photos: { title: 'Photos' },
        notes: { title: 'Notes' }
      }
    }
  }
}
</script>

<style lang="scss">
#update-work-order .my-grid-class {
  height: 400px;
}

#update-work-order tbody {
  min-height: 100px !important;
  overflow: auto !important;
}
</style>
